import React from 'react';

import { Box, Center, HStack, Text } from '@chakra-ui/react';
import round from 'lodash/round';
import { colorOpacity, customTheme } from 'theme';

import {
  CustomTooltip,
  DetailsIdentifier,
  getIcon,
  Tag,
  WithResourceIcon,
} from 'components/DataDisplay';
import { IconTypes } from 'components/Icons';
import { toTitleCase } from 'utils/string';

export const resourceVulnerabilityKey = (
  instanceId: string,
  containerId: string,
) => {
  return `${instanceId}-${containerId}`;
};

export const renderResourceState = (state: string) => (
  <WithResourceIcon
    resourceType={state}
    iconFilled={false}
    iconSize="regular"
    spacing={1}
  >
    {toTitleCase(state)}
  </WithResourceIcon>
);

export const renderIsAMIPublic = value => (
  <WithResourceIcon
    resourceType={IconTypes.Globe}
    iconFilled={false}
    iconSize="regular"
    color={
      !!value ? customTheme.colors.critical : customTheme.colors.gray['250']
    }
    spacing={1}
  >
    {!!value ? 'Yes' : 'No'}
  </WithResourceIcon>
);

export const renderCvssScore = row => {
  const value = row?.cvss_v3?.score ?? row?.cvss_v4?.score ?? row?.CVSS2_SCORE;
  const label = !!row?.cvss_v3?.score
    ? 'CVSS V3: ' + value
    : !!row?.cvss_v4?.score
    ? 'CVSS V4: ' + value
    : 'CVSS: ' + value;
  const severity = row?.severity?.toLowerCase();
  return (
    <CustomTooltip label={label}>
      <Center pr={10}>
        {value || value === 0 ? (
          <Tag
            label={round(value, 1)}
            styles={{
              label: { color: customTheme.colors?.[severity] },
              tag: {
                border: 'none',
                bg: colorOpacity(customTheme.colors?.[severity], 0.1),
              },
            }}
          />
        ) : (
          '-'
        )}
      </Center>
    </CustomTooltip>
  );
};

export const renderVulnerabilityCloudIDField = resource_id => {
  return (
    <DetailsIdentifier
      label="Cloud ID"
      value={<Text noOfLines={1}>{resource_id}</Text>}
      tooltip={resource_id}
    />
  );
};

export const renderVulnerabilityOSField = (name, version) => {
  return (
    <DetailsIdentifier
      label="Operating System"
      value={
        <HStack>
          <Box boxSize={5}>
            <Center>{getIcon(name!)}</Center>
          </Box>
          <Box>
            {name} {version}
          </Box>
        </HStack>
      }
    />
  );
};

export const renderVulnerabilityImageField = image => {
  return (
    <DetailsIdentifier
      label="Image"
      value={
        <Text whiteSpace="nowrap" textOverflow="ellipsis" w="full">
          {image}
        </Text>
      }
      tooltip={image}
    />
  );
};

export const renderScanOnPush = value => {
  const color = !!value
    ? customTheme.colors.green['300']
    : customTheme.colors.critical;

  return (
    <Tag
      label={!!value ? 'Enabled' : 'Disabled'}
      styles={{
        tag: {
          bg: colorOpacity(color, 0.1),
          border: 'none',
          borderRadius: 'full',
        },
        label: { color },
      }}
    />
  );
};
